import { isEmpty } from '@intelligence/v3/utils/common';
import {
  getDefaultApiFieldMap,
  getDefaultUrlFieldMap,
  getItemApiParamsFieldMap,
  getItemUrlParamsFieldMap,
} from '../shared/fieldMapUtils';
import { getValidRevenueGrowthFilterValue } from './utils';

import type { IFilterMiddlewareUtils } from '../shared/types';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IRevenueGrowthFilterValue } from './types';

const getApiFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  optField: string;
  valueField: string;
} => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);

  return {
    optField: apiParamsFieldMap?.opt || defaultApiFieldMap.opt || 'revenue_growth_compare_opt',
    valueField: apiParamsFieldMap?.value || defaultApiFieldMap.value || 'revenue_growth_compare_value',
  };
};

const getUrlFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  optField: string;
  valueField: string;
} => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);

  return {
    optField: urlParamsFieldMap?.opt || defaultUrlFieldMap.opt || 'revenueGrowthCompareOpt',
    valueField: urlParamsFieldMap?.value || defaultUrlFieldMap.value || 'revenueGrowthCompareValue',
  };
};

/**
 * 收入增长筛选器中间件工具
 * value 类型：IComparableValue { opt: string; value: string | number }
 *
 * 校验规则：
 * - opt 必须为非空字符串（比较符）
 * - value 必须为非空字符串或有效数字
 * - opt 或 value 任一无效时返回 null
 */
class RevenueGrowthMiddlewareUtils implements IFilterMiddlewareUtils<IRevenueGrowthFilterValue, IRecord<any>> {
  convertToApiParams(
    item: IFilterItemConfig,
    filterValue?: IRevenueGrowthFilterValue,
    params?: unknown,
  ): IRecord<any> | null {
    const normalizedValue = getValidRevenueGrowthFilterValue(filterValue);

    if (!normalizedValue) {
      return null;
    }

    const { optField, valueField } = getApiFieldMap(item, params);

    return {
      [optField]: normalizedValue.opt,
      [valueField]: normalizedValue.value,
    };
  }

  getManagedUrlKeys(item: IFilterItemConfig, params?: unknown): string[] {
    const { optField, valueField } = getUrlFieldMap(item, params);

    return [optField, valueField].filter(Boolean);
  }

  serializeUrlParams(
    item: IFilterItemConfig,
    filterValue?: IRevenueGrowthFilterValue,
    params?: unknown,
  ): IRecord<string> | null {
    const normalizedValue = getValidRevenueGrowthFilterValue(filterValue);

    if (!normalizedValue) {
      return null;
    }

    const { optField, valueField } = getUrlFieldMap(item, params);

    return {
      [optField]: normalizedValue.opt,
      [valueField]: String(normalizedValue.value),
    };
  }

  deserializeUrlParams(
    item: IFilterItemConfig,
    searchParams: URLSearchParams,
    params?: unknown,
  ): IRevenueGrowthFilterValue | null {
    if (isEmpty(searchParams)) {
      return null;
    }

    const { optField, valueField } = getUrlFieldMap(item, params);
    const opt = searchParams.get(optField) || '';
    const value = searchParams.get(valueField) || '';
    const normalizedValue = getValidRevenueGrowthFilterValue({ opt, value });

    if (!normalizedValue) {
      return null;
    }

    return normalizedValue;
  }
}

export default RevenueGrowthMiddlewareUtils;
